import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTechnologyStatus } from '@/hooks/useTechnologyStatus';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/useAuth';
import { HUDStatus } from '@/components/ui/HUDStatus';
import { createComponentLogger } from '@/utils/logger';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

const log = createComponentLogger('TechnologyStatusBar', 'ui');

interface TechnologyStatusBarProps {
  pageTitle: string;
  includeRealtime?: boolean;
  className?: string;
  variant?: 'default' | 'compact';
}

export const TechnologyStatusBar: React.FC<TechnologyStatusBarProps> = ({ 
  pageTitle, 
  includeRealtime = false,
  className,
  variant = 'default'
}) => {
  const { status, refreshStatus } = useTechnologyStatus(includeRealtime);
  const { signInWithGoogle } = useAuth();
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);


  const handleConnectGmail = async () => {
    setIsConnectingGmail(true);
    try {
      await signInWithGoogle();
      // The page will reload after OAuth, so no need to handle the response
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      setIsConnectingGmail(false);
    }
  };

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      // Use the new refresh function from the hook
      await refreshStatus();
      
      // Trigger a custom event for any other listeners
      window.dispatchEvent(new CustomEvent('gmail-status-refresh'));
      
      // Show feedback to user
      log.info('Gmail status refreshed successfully');
    } catch (error) {
      console.error('Error refreshing Gmail status:', error);
      log.error('Failed to refresh Gmail status', undefined, error as Error);
    } finally {
      // Add a small delay to show the loading state
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  // If Gmail is not connected, we might want to show a connect button
  if (!status.gmail.connected && !status.overall.operational) {
    return (
      <div className={cn("relative flex items-center w-full", className)}>
        <h1 className="text-3xl font-bold text-foreground absolute left-0">{pageTitle}</h1>
        <div className="flex-1 flex justify-center">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-left"
              >
                <HUDStatus
                  status={status}
                  includeRealtime={includeRealtime}
                  className={variant === 'compact' ? 'lw-technology-status--compact' : undefined}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                {!status.gmail.connected && (
                  <div>
                    <h4 className="font-medium mb-2">Connect Gmail</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Connect Gmail to send emails directly from the application.
                    </p>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={handleConnectGmail}
                      disabled={isConnectingGmail}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {isConnectingGmail ? 'Connecting...' : 'Connect Gmail'}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative flex items-center w-full", className)}>
      <h1 className="text-3xl font-bold text-foreground absolute left-0">{pageTitle}</h1>
      <div className="flex-1 flex justify-center">
        <HUDStatus 
          status={status} 
          includeRealtime={includeRealtime} 
          onRefresh={handleRefreshStatus}
          isRefreshing={isRefreshing}
          className={variant === 'compact' ? 'lw-technology-status--compact' : undefined}
        />
      </div>
    </div>
  );
};
